import { getCriteria } from './tags';
import { cloneDeep } from 'lodash';
import { getNodeStepName } from '../helpers/node';

/**
 * Sanitizes the settings generated for the node to use in the call flow so they can be sent to the backend
 * @param {*} settings node settings to sanitize
 * @returns sanitized settings that can be used on the backend request
 */
export function sanitizeNodeSettings(settings) {
  const sanitizedSettings = {};
  for (const settingKey in settings) {

    const setting = settings[settingKey];

    if (setting !== null && setting !== undefined && settingKey !== 'isEnabled') {

      if (typeof setting !== 'object') {
        // Convert empty string to null else API treats it as valid value
        sanitizedSettings[settingKey] = setting !== '' ? setting : null;
        continue;
      }

      if (typeof setting === 'object' && settingKey == 'hourseOfOperationSetting') {
        sanitizedSettings[settingKey] = setting;
        continue;
      }

      if (typeof setting === 'object' && (setting.isEnabled == null || setting.isEnabled)) {
        sanitizedSettings[settingKey] = sanitizeNodeSettings(setting);
        continue;
      }
    }
  }
  return sanitizedSettings;
}

/**
 * Converts The call flow Scene into an IVR to save on the backend
 * @param {*} callFlowId Id of the tree
 * @param {*} scene Call flow scene we want to convert
 * @returns IVR backend request object
 */
export function getTreeSaveObject(callFlowId, scene) {
  const nodesCloned = cloneDeep(scene.nodes);
  const linksCloned = cloneDeep(scene.links);
  const nodesMap = {};

  for (const node of nodesCloned) {
    node.posX = node.x;
    node.posY = node.y;
    node.settings = sanitizeNodeSettings(node.settings);
    node.stepName = getNodeStepName(node);
    nodesMap[node.id] = { node };
    nodesMap[node.id].childLinks = nodesMap[node.id].childLinks ?? [];

    // We need this hidden setting so if the user presses an invalid digit they can retry
    // We clean it up here to readd using the link filters
    if (node.nodeTypeId == 'MenuNode') {
      nodesMap[node.id].node.settings.menuItems = '';
    }

    // We need to insert a route to the go to node
    if (node.nodeTypeId == 'GoToNode') {
      const goToNodeId = nodesMap[node.id].node.settings.goToNodeId;
      const nodeExists = nodesCloned.find((nodeToCheck) => nodeToCheck.id == goToNodeId);
      if (goToNodeId && nodeExists) {
        nodesMap[node.id].childLinks.push({
          childNodeId: nodesMap[node.id].node.settings.goToNodeId,
          isCriteriaInverted: false,
          onSuccess: true
        });
      }
    }
  }

  for (const link of linksCloned) {
    nodesMap[link.originNode.id].childLinks.push({
      childNodeId: link.targetNode.id,
      criteria: getCriteria(link),
      isCriteriaInverted: link.isCriteriaInverted,
      name: link.name,
      weight: link.weight,
      onSuccess: link.onSuccess ?? !link.isOnFailure,
    });

    // We need this hidden setting so if the user presses an invalid digit they can retry
    if (link.originNode.nodeTypeId == 'MenuNode' && link.filter != null) {
      nodesMap[link.originNode.id].node.settings.menuItems += link.filter;
    }
  }

  const root = nodesMap['root'].node;

  const rootChild = nodesMap['root'].childLinks;

  root.childLinks = rootChild;

  delete nodesMap['root'];

  return {
    centerX: scene.container.centerX,
    centerY: scene.container.centerY,
    nodes: nodesMap,
    root: root,
    id: callFlowId,
    zoom: scene.container.scale,
    name: scene.name,
    // version 1 are ivrs, version 2 are call flows
    version: 2
  };
}